import { motion } from 'motion/react';
import { CheckCircle, Copy, Share2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/app/context/AuthContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { API_ENDPOINTS } from '@/config/api';
import { trackCheckoutComplete, trackExtensionInstallClick, trackReferralLinkCopied } from '@/lib/analytics';
import { sendLicenseToExtension } from '@/lib/extensionBridge';

export function SuccessPage() {
  const [searchParams] = useSearchParams();
  const { updateLicense, user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const [licenseKey, setLicenseKey] = useState<string | null>(() => searchParams.get('licenseKey'));
  const [planName, setPlanName] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const extensionSent = searchParams.get('extensionSent') === 'true';

  // Fetch referral code for the signed-in user
  useEffect(() => {
    if (!user?.email) return;
    fetch(`${API_ENDPOINTS.REFERRALS}?email=${encodeURIComponent(user.email)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.referral_code) setReferralCode(data.referral_code);
      })
      .catch(() => {});
  }, [user?.email]);

  useEffect(() => {
    document.title = 'Success - xRepl.ai';
  }, []);

  // After Stripe checkout redirect: fetch license key from session_id
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || licenseKey) return;

    let cancelled = false;
    setSessionLoading(true);
    setSessionError(null);
    fetch(`${API_ENDPOINTS.SUBSCRIBE_SESSION_SUCCESS}?session_id=${encodeURIComponent(sessionId)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success && data.license_key) {
          setLicenseKey(data.license_key);
          setPlanName(data.plan || null);
          // Track checkout completion
          trackCheckoutComplete(data.plan || 'unknown');
          // Directly update the license in AuthContext and localStorage
          // This ensures the Account page shows the correct plan immediately
          if (data.plan) {
            updateLicense(data.license_key, data.plan);
          }
          // Always try sending to extension (works if extension is installed)
          sendLicenseToExtension(data.license_key, data.plan || 'free');
        } else {
          setSessionError(data.error || 'Could not load license');
        }
      })
      .catch((err) => {
        if (!cancelled) setSessionError(err.message || 'Failed to load license');
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false);
      });
    return () => { cancelled = true; };
  }, [searchParams, licenseKey, updateLicense]);

  const copyToClipboard = () => {
    if (licenseKey) {
      navigator.clipboard.writeText(licenseKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3A8A] via-[#0F2847] to-[#1E3A8A]">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <CheckCircle size={80} className="text-green-500 mx-auto mb-6" />
            
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Welcome to xRepl.ai!
            </h1>
            
            <p className="text-xl text-white/80 mb-8">
              Your subscription is now active.{' '}
              {sessionLoading ? 'Loading your license key…' : licenseKey ? 'Your license key is below.' : sessionError ? null : 'Check your email for your license key and next steps to get started.'}
            </p>

            {sessionError && (
              <p className="text-amber-300 mb-4" role="alert">
                {sessionError} You can find your license key in your account or email.
              </p>
            )}

            {licenseKey && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6 border border-white/20">
                <h2 className="text-xl font-semibold text-white mb-3">
                  Your License Key
                </h2>
                <div className="bg-white/5 rounded-lg p-4 border border-white/10 flex items-center justify-between gap-4">
                  <code className="text-lg font-mono font-bold text-[#60A5FA] flex-1 text-center break-all">
                    {licenseKey}
                  </code>
                  <button
                    onClick={copyToClipboard}
                    className="flex-shrink-0 px-4 py-2 bg-[#60A5FA] hover:bg-[#60A5FA]/90 text-white rounded-lg transition-all flex items-center gap-2"
                    title="Copy to clipboard"
                  >
                    <Copy size={18} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-sm text-white/60 mt-3 text-center">
                  {extensionSent
                    ? 'This key has been automatically sent to your xReplAI extension!'
                    : 'This key has also been sent to your email address.'}
                </p>
              </div>
            )}

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20">
              <h2 className="text-2xl font-semibold text-white mb-4">
                What's Next?
              </h2>
              <ul className="text-left text-white/80 space-y-3">
                {licenseKey && (
                  <li className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="text-left hover:bg-white/10 rounded-lg px-2 py-1 -mx-2 transition-colors flex items-center gap-2 flex-wrap group"
                      title="Click to copy"
                    >
                      <code className="text-[#60A5FA] font-mono text-sm break-all">{licenseKey}</code>
                      <Copy size={16} className="text-white/60 group-hover:text-[#60A5FA] flex-shrink-0" />
                    </button>
                  </li>
                )}
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                  <span>Install the xRepl.ai Chrome extension from the Chrome Web Store</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                  <span>
                    {extensionSent
                      ? 'Your extension has been activated automatically — you\'re all set!'
                      : 'Open the extension and enter your license key when prompted'}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                  <span>Start drafting personalized email replies with AI</span>
                </li>
              </ul>
            </div>

            {/* Referral section */}
            {referralCode && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Share2 size={20} className="text-[#60A5FA]" />
                  <h2 className="text-xl font-semibold text-white">
                    Share xReplAI, Earn Rewards
                  </h2>
                </div>
                <p className="text-sm text-white/70 mb-4">
                  Share your referral link with colleagues. They get 60 free generations (instead of 30), and you earn a free month of Pro for every 3 paid conversions.
                </p>
                <div className="bg-white/5 rounded-lg p-3 border border-white/10 flex items-center justify-between gap-3">
                  <code className="text-sm font-mono text-[#60A5FA] flex-1 text-center truncate">
                    xrepl.ai/?ref={referralCode}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`https://xrepl.ai/?ref=${referralCode}`);
                      setReferralCopied(true);
                      trackReferralLinkCopied();
                      setTimeout(() => setReferralCopied(false), 2000);
                    }}
                    className="flex-shrink-0 px-3 py-1.5 bg-[#60A5FA] hover:bg-[#60A5FA]/90 text-white rounded-lg transition-all text-sm flex items-center gap-1.5"
                  >
                    <Copy size={14} />
                    {referralCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://chromewebstore.google.com/detail/aeemmefmpkgmokecemoedojagcecabmj"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExtensionInstallClick('success_page')}
                className="inline-flex items-center justify-center px-8 py-4 bg-[#60A5FA] text-white font-semibold rounded-lg hover:bg-[#60A5FA]/90 transition-all shadow-lg"
              >
                Download Extension
              </a>
              <a
                href="/"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/20 text-white font-semibold rounded-lg hover:bg-white/30 transition-all border border-white/30"
              >
                Back to Home
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
